import { Box, Tooltip, Typography } from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import { getImageName, mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import {
  BoldSpan,
  MutedTypography,
  StyledAssetCardItemHeader,
  StyledCheckBox,
  UnderlinedSpan,
} from './AssetCard.styles';
import useAppTheme from 'hooks/useAppTheme';
import { resolveAssetTypeDisplay } from 'utils/helpers/assets';
import type { Asset } from 'types/asset.types';
import { timeAgo } from '../utils';
import AssetOnlineStatus from './AssetOnlineStatus';
import { convertUtcToDisplayTime, formatDate } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { assetHasDTC } from 'views/FleetOverview/helpers/asset.helpers';
import AssetDTCNotifications from './AssetDTCNotifications';
import { useMemo } from 'react';
import LaunchCompareModal from './LaunchCompareModal';
import type { RuleNotifications } from 'types/notification.types';
import AssetCardNotificationChip from './AssetCardNotificationChip';
import { useAppSelector } from 'store/hook';
import { PNG } from 'components/Asset/PNG';
import { useTranslation } from 'react-i18next';

interface Props {
  asset: Asset;
  showCheckedButton: boolean;
  handleCheckBoxSelected?: any;
  checkedList: string[];
  handleAssetSelected: (assetId: string) => void;
  assetNotifications: RuleNotifications[];
}

export function AssetCardItemHeader({
  asset,
  showCheckedButton,
  handleCheckBoxSelected,
  checkedList,
  handleAssetSelected,
  assetNotifications,
}: Props) {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const isLoadingAllAssets = useAppSelector(
    (state) => state.assetReducer.isLoadingAllAssets
  );
  const { assetId, assetType, bumperNumber, make, model, device } = asset;
  const assetTypeResolved = resolveAssetTypeDisplay(assetType);

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const hasDTC = useMemo(() => assetHasDTC(asset), [asset]);

  const isChecked = useMemo(() => checkedList.includes(assetId), [checkedList]);

  const png = useMemo(() => {
    return getImageName(asset, 'Small');
  }, [asset]);

  return (
    <StyledAssetCardItemHeader
      enabledarktheme={enableDarkTheme}
      id="assetCardHeader"
    >
      {hasDTC && (
        <AssetDTCNotifications
          bumperNumber={bumperNumber}
          isActive={asset.isActive}
        />
      )}
      {!showCheckedButton && !isLoadingAllAssets ? (
        <LaunchCompareModal
          asset={asset}
          handleAssetSelected={handleAssetSelected}
          hasDTC={hasDTC}
        />
      ) : null}
      <div
        id="assetCardSvg"
        style={{
          gridColumn: 'span 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        {showCheckedButton ? (
          <StyledCheckBox
            enabledarktheme={enableDarkTheme}
            checked={isChecked}
            onChange={(e) => {
              if (!e.target.value) return;
              handleCheckBoxSelected(assetId);
            }}
          />
        ) : null}
        {png ? (
          <PNG
            name={png}
            style={{
              height: '60px',
              width: '90px',
            }}
          />
        ) : (
          <SVG
            name={mapVehiclesAssets(assetType)}
            style={{
              fill: '#E2C171',
              height: '30px',
              width: showCheckedButton ? '40px' : '60px',
              // paddingLeft: '10px',
              // paddingTop: '10px',
            }}
          />
        )}
      </div>
      <div
        style={{
          gridColumn: 'span 3',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          title={`Device Status: ${device.deviceStatus}`}
          placement="bottom"
          arrow
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content',
            }}
          >
            <>
              <Typography
                style={{
                  color: enableDarkTheme ? '#fff' : '#000',
                  fontSize: '14px',
                  fontWeight: 'bolder',
                  margin: '0px !important',
                  padding: '0px !important',
                }}
              >
                <UnderlinedSpan>{bumperNumber}</UnderlinedSpan>
              </Typography>
              <AssetOnlineStatus deviceStatus={device.deviceStatus} />
            </>
          </div>
        </Tooltip>
        <div>
          {/* slightly muted, and smaller text */}
          {/* OEM */}
          <MutedTypography
            enabledarktheme={enableDarkTheme}
            style={{
              margin: '0px !important',
              padding: '0px !important',
            }}
          >
            {t('type')} {assetTypeResolved}
          </MutedTypography>

          <MutedTypography
            enabledarktheme={enableDarkTheme}
            style={{
              fontWeight: 'bold',
            }}
          >
            <BoldSpan>{t('oem')}</BoldSpan> {make}
          </MutedTypography>
          <MutedTypography
            enabledarktheme={enableDarkTheme}
            style={{
              fontWeight: 'bold',
            }}
          >
            <BoldSpan>{t('model')}</BoldSpan> {model}
          </MutedTypography>
          <MutedTypography enabledarktheme={enableDarkTheme}>
            {t('last_connected')} {timeAgo(device.ingestDate)}
          </MutedTypography>
          {device.last_location && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <MutedTypography enabledarktheme={enableDarkTheme}>
                {t('last_location')} {device.last_location}
              </MutedTypography>
              {device.last_location_ts && (
                <MutedTypography
                  enabledarktheme={enableDarkTheme}
                  style={{ fontSize: '10px' }}
                >
                  {t('last_location_time')} {timeAgo(device.last_location_ts)}
                </MutedTypography>
              )}
            </div>
          )}
          <Tooltip
            placement="bottom"
            arrow
            title={
              !asset.isActive && asset.assetNotes?.length > 0
                ? `Latest Note: ${asset.assetNotes[0].values} 
                        ${convertUtcToDisplayTime(
                          asset.assetNotes[0].date_added,
                          preferredTimeDisplayMode,
                          timezone,
                          'DD/MM/YYYY HH:mm:ss'
                        )}
                          `
                : 'Operational'
            }
          >
            <MutedTypography enabledarktheme={enableDarkTheme}>
              {t('asset_status')}{' '}
              <UnderlinedSpan>
                {asset.isActive ? 'Active' : 'Inactive'}
              </UnderlinedSpan>
            </MutedTypography>
          </Tooltip>
        </div>
      </div>
      <Box
        sx={{
          gridColumn: {
            xs: 'span 4',
            xl: 'span 4',
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: {
            xs: '0px',
            xl: '-15%',
          },
        }}
      >
        <AssetCardNotificationChip assetNotifications={assetNotifications} />
      </Box>
    </StyledAssetCardItemHeader>
  );
}

export default AssetCardItemHeader;
